import React, { useState, useCallback, useId, useMemo } from "react";
import { useAuthFetch } from "../lib/authFetch";
import { useCorpora, corpusLabel } from "../lib/useCorpora";

const INDEXING_API = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";

// ── Types ─────────────────────────────────────────────────────────────────────

type Comparator = "equals" | "contains" | "matches";

interface MetadataFilter {
  id:         string; // local UI key only
  property:   string;
  comparator: Comparator;
  value:      string;
}

interface DocumentResult {
  fileId:           string;
  owner:            string;
  originalFilename: string;
  newFilename?:     string | null;
  documentType?:    string | null;
  status:           string;
  processedAt:      string;
  parsedS3Key?:     string | null;
  analysisS3Key?:   string | null;
  metadata:         Record<string, unknown>;
}

interface DocumentsResponse {
  total:     number;
  page:      number;
  perPage:   number;
  documents: DocumentResult[];
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function Chip({ label, colour = "gray" }: { label: string; colour?: "gray" | "blue" | "violet" | "emerald" | "amber" }) {
  const cls: Record<string, string> = {
    gray:    "bg-paper text-inklight",
    blue:    "bg-blue-100 text-blue-700",
    violet:  "bg-violet-100 text-violet-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber:   "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls[colour]}`}>{label}</span>
  );
}

function statusColour(status: string) {
  if (status === "complete") return "emerald";
  if (status === "failed")   return "amber";
  return "gray";
}

function TextField({
  label, id, value, onChange, placeholder,
}: {
  label: string; id: string; value: string;
  onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-kslate mb-0.5">{label}</label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-krule rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-kamber/70"
      />
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1 min-w-0">
      <span className="text-kfaint shrink-0">{label}:</span>
      <span className="text-inklight truncate">{value}</span>
    </div>
  );
}

// ── Tree view ───────────────────────────────────────────────────────────────

type ViewMode = "list" | "tree";

interface AuthorGroup {
  name: string;
  docs: DocumentResult[];
}

interface DocTypeGroup {
  docType:  string;
  authors:  AuthorGroup[];
}

function authorLabel(doc: DocumentResult): string {
  const m = doc.metadata ?? {};
  const author = typeof m.author === "string" ? m.author.trim() : "";
  if (author) return author;
  const entity = typeof m.entityName === "string" ? m.entityName.trim() : "";
  if (entity) return entity;
  return "(Unknown)";
}

function buildTree(documents: DocumentResult[]): DocTypeGroup[] {
  const typeMap = new Map<string, Map<string, DocumentResult[]>>();
  for (const doc of documents) {
    const dt = doc.documentType?.trim() || "(No type)";
    const au = authorLabel(doc);
    if (!typeMap.has(dt)) typeMap.set(dt, new Map());
    const authorMap = typeMap.get(dt)!;
    if (!authorMap.has(au)) authorMap.set(au, []);
    authorMap.get(au)!.push(doc);
  }
  return Array.from(typeMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([docType, authorMap]) => ({
      docType,
      authors: Array.from(authorMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, docs]) => ({ name, docs })),
    }));
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
      fill="none" stroke="currentColor" viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function TreeDocItem({ doc }: { doc: DocumentResult }) {
  const [expanded, setExpanded] = useState(false);
  const meta = doc.metadata ?? {};
  const metaEntries = Object.entries(meta).filter(
    ([k]) => !["pageCount", "sectionCount", "rawSectionCount", "analysisUnit", "model",
               "factCount", "indexType", "format", "taxonomyCount", "author", "entityName"].includes(k)
  );

  return (
    <div className="border border-krule/60 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-paper/50 transition-colors"
      >
        <ChevronIcon open={expanded} />
        <span className="flex-1 min-w-0 text-xs font-medium text-inklight truncate">
          {doc.newFilename ?? doc.originalFilename}
        </span>
        <Chip label={doc.status} colour={statusColour(doc.status)} />
        <span className="text-xs text-kfaint shrink-0">
          {doc.processedAt ? new Date(doc.processedAt).toLocaleDateString() : ""}
        </span>
      </button>

      {expanded && (
        <div className="px-4 py-2 border-t border-krule/60 bg-paper/50 space-y-1">
          {doc.newFilename && (
            <p className="text-xs text-kfaint">{doc.originalFilename}</p>
          )}
          {metaEntries.length > 0 && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
              {metaEntries.map(([k, v]) => (
                <MetaRow key={k} label={k} value={Array.isArray(v) ? v.join(", ") : String(v ?? "")} />
              ))}
            </div>
          )}
          <p className="text-xs text-krule font-mono truncate pt-1">{doc.fileId}</p>
        </div>
      )}
    </div>
  );
}

function AuthorNode({ group }: { group: AuthorGroup }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="ml-4 border-l border-krule pl-3">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex items-center gap-1.5 py-1.5 text-left w-full group"
      >
        <ChevronIcon open={open} />
        <span className="text-xs font-semibold text-kslate group-hover:text-ink">{group.name}</span>
        <span className="ml-1 text-xs text-kfaint">({group.docs.length})</span>
      </button>
      {open && (
        <div className="space-y-1 pb-2">
          {group.docs.map(doc => <TreeDocItem key={doc.fileId} doc={doc} />)}
        </div>
      )}
    </div>
  );
}

function DocTypeNode({ group }: { group: DocTypeGroup }) {
  const [open, setOpen] = useState(true);
  const total = group.authors.reduce((s, a) => s + a.docs.length, 0);
  return (
    <div className="bg-white border border-krule rounded-xl overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-paper/50 transition-colors"
      >
        <ChevronIcon open={open} />
        <span className="flex-1 text-sm font-semibold text-ink">{group.docType}</span>
        <span className="text-xs text-kfaint">{total} doc{total !== 1 ? "s" : ""}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 divide-y divide-krule/60">
          {group.authors.map(a => <AuthorNode key={a.name} group={a} />)}
        </div>
      )}
    </div>
  );
}

// ── Metadata filter row ─────────────────────────────────────────────────────────

function FilterRow({
  filter, onChange, onRemove,
}: {
  filter: MetadataFilter;
  onChange: (f: MetadataFilter) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex gap-1 items-center">
      <input
        type="text"
        placeholder="property"
        value={filter.property}
        onChange={e => onChange({ ...filter, property: e.target.value })}
        className="w-28 border border-krule rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-kamber/70"
      />
      <select
        value={filter.comparator}
        onChange={e => onChange({ ...filter, comparator: e.target.value as Comparator })}
        className="border border-krule rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-kamber/70 bg-white"
      >
        <option value="equals">equals</option>
        <option value="contains">contains</option>
        <option value="matches">matches</option>
      </select>
      <input
        type="text"
        placeholder="value"
        value={filter.value}
        onChange={e => onChange({ ...filter, value: e.target.value })}
        className="flex-1 min-w-0 border border-krule rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-kamber/70"
      />
      <button
        type="button"
        onClick={onRemove}
        className="text-kfaint hover:text-red-500 px-1 text-sm leading-none"
        title="Remove filter"
      >×</button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

let _nextId = 1;
function newFilterId() { return `f${_nextId++}`; }

const DocumentsPage: React.FC = () => {
  const [documentType,  setDocumentType]  = useState("");
  const [corpusId,      setCorpusId]      = useState("");
  const [metaFilters,   setMetaFilters]   = useState<MetadataFilter[]>([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [response,      setResponse]      = useState<DocumentsResponse | null>(null);
  const [hasSearched,   setHasSearched]   = useState(false);
  const [viewMode,      setViewMode]      = useState<ViewMode>("list");
  const uid = useId();
  const authFetch = useAuthFetch();
  const { accessible: accessibleCorpora } = useCorpora();

  const treeData = useMemo<DocTypeGroup[]>(
    () => (response ? buildTree(response.documents) : []),
    [response],
  );

  const addFilter = useCallback(() => {
    setMetaFilters(prev => [...prev, { id: newFilterId(), property: "", comparator: "equals", value: "" }]);
  }, []);

  const updateFilter = useCallback((id: string, updated: MetadataFilter) => {
    setMetaFilters(prev => prev.map(f => f.id === id ? updated : f));
  }, []);

  const removeFilter = useCallback((id: string) => {
    setMetaFilters(prev => prev.filter(f => f.id !== id));
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    // Corpus scope is derived server-side from the caller's credentials; an
    // explicit corpusId narrows to a single accessible corpus.
    const params = new URLSearchParams();
    if (corpusId) params.set("corpusId", corpusId);
    if (documentType.trim()) params.set("documentType", documentType.trim());

    const validFilters = metaFilters.filter(f => f.property.trim() && f.value.trim());
    if (validFilters.length > 0) {
      params.set(
        "filters",
        JSON.stringify(validFilters.map(({ property, comparator, value }) => ({ property, comparator, value }))),
      );
    }

    try {
      const res = await authFetch(`${INDEXING_API}/query/documents?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      const data: DocumentsResponse = await res.json();
      setResponse(data);
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setDocumentType("");
    setCorpusId("");
    setMetaFilters([]);
    setResponse(null);
    setError(null);
    setHasSearched(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 h-[78px] border-b border-krule bg-white shrink-0">
        <div>
          <h2 className="text-xl font-light tracking-tight text-ink">Search Docs List</h2>
          <p className="text-xs font-mono text-kslate mt-0.5">Query processed_documents by type and metadata</p>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* ── Filter sidebar ─────────────────────────────────────────────── */}
      <aside className="w-80 shrink-0 border-r border-krule bg-paper/50 flex flex-col overflow-y-auto">
        <div className="px-4 py-4 border-b border-krule">
          <h1 className="text-base font-medium text-ink">Document Filter</h1>
        </div>

        <form onSubmit={handleSearch} className="flex-1 flex flex-col px-4 py-4 gap-3">
          {/* Fixed filters */}
          <div>
            <label className="block text-xs font-medium text-inklight mb-1" htmlFor={`${uid}-corpus`}>Corpus</label>
            <select
              id={`${uid}-corpus`}
              value={corpusId}
              onChange={e => setCorpusId(e.target.value)}
              className="w-full text-sm border border-krule rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70"
            >
              <option value="">All corpora</option>
              {accessibleCorpora.map(c => (
                <option key={c.corpusId} value={c.corpusId}>{corpusLabel(c)}</option>
              ))}
            </select>
          </div>
          <TextField label="Document type" id={`${uid}-doctype`} value={documentType}
            onChange={setDocumentType} placeholder="edgar-companyfacts, Third-party research…" />

          {/* Dynamic metadata filters */}
          <hr className="border-krule" />
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-kfaint uppercase tracking-wide">Metadata filters</p>
            <button
              type="button"
              onClick={addFilter}
              className="text-xs text-ink hover:text-inklight font-medium"
            >+ Add</button>
          </div>

          {metaFilters.length === 0 && (
            <p className="text-xs text-kfaint italic">No filters — click + Add to filter on metadata properties</p>
          )}

          {metaFilters.map(f => (
            <FilterRow
              key={f.id}
              filter={f}
              onChange={updated => updateFilter(f.id, updated)}
              onRemove={() => removeFilter(f.id)}
            />
          ))}

          <div className="flex gap-2 pt-1 mt-auto">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-ink hover:bg-inklight disabled:bg-ink/30 text-white text-sm font-medium rounded-lg px-3 py-2 transition-colors"
            >
              {loading ? "Searching…" : "Search"}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="px-3 py-2 text-sm text-kslate hover:text-inklight border border-krule rounded-lg hover:bg-paper transition-colors"
            >
              Clear
            </button>
          </div>
        </form>
      </aside>

      {/* ── Results panel ──────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto px-6 py-5">
        {/* Header */}
        {response && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-baseline gap-2">
              <h2 className="text-base font-medium text-ink">Results</h2>
              <span className="text-sm text-kslate">
                {response.total} document{response.total !== 1 ? "s" : ""} found
              </span>
            </div>
            {/* View mode toggle */}
            <div className="flex rounded-lg border border-krule overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 transition-colors ${
                  viewMode === "list"
                    ? "bg-ink text-white"
                    : "bg-white text-kslate hover:bg-paper/50"
                }`}
              >List</button>
              <button
                type="button"
                onClick={() => setViewMode("tree")}
                className={`px-3 py-1.5 border-l border-krule transition-colors ${
                  viewMode === "tree"
                    ? "bg-ink text-white"
                    : "bg-white text-kslate hover:bg-paper/50"
                }`}
              >Tree</button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {/* Empty state */}
        {!loading && !error && !hasSearched && (
          <div className="flex flex-col items-center justify-center h-64 text-kfaint">
            <svg className="w-10 h-10 mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">Set filters and click Search</p>
          </div>
        )}

        {hasSearched && !loading && response?.documents.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-kfaint">
            <p className="text-sm">No documents matched your filters.</p>
          </div>
        )}

        {/* Tree view */}
        {response && response.documents.length > 0 && viewMode === "tree" && (
          <div className="space-y-3">
            {treeData.map(g => <DocTypeNode key={g.docType} group={g} />)}
          </div>
        )}

        {/* Document cards (list view) */}
        {response && response.documents.length > 0 && viewMode === "list" && (
          <div className="space-y-3">
            {response.documents.map((doc) => {
              const meta = doc.metadata ?? {};
              const metaEntries = Object.entries(meta).filter(
                ([k]) => !["pageCount", "sectionCount", "rawSectionCount", "analysisUnit", "model",
                           "factCount", "indexType", "format", "taxonomyCount"].includes(k)
              );

              return (
                <div key={doc.fileId}
                  className="bg-white border border-krule rounded-xl px-5 py-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink leading-snug truncate">
                        {doc.newFilename ?? doc.originalFilename}
                      </p>
                      {doc.newFilename && (
                        <p className="text-xs text-kfaint truncate">{doc.originalFilename}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <Chip label={doc.status} colour={statusColour(doc.status)} />
                      <span className="text-xs text-kfaint">
                        {doc.processedAt ? new Date(doc.processedAt).toLocaleDateString() : "—"}
                      </span>
                    </div>
                  </div>

                  {/* Document type */}
                  {doc.documentType && (
                    <div className="mb-2">
                      <Chip label={doc.documentType} colour="blue" />
                    </div>
                  )}

                  {/* Key metadata grid */}
                  {metaEntries.length > 0 && (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-kslate mb-2">
                      {metaEntries.map(([k, v]) => (
                        <MetaRow key={k} label={k} value={Array.isArray(v) ? v.join(", ") : String(v ?? "")} />
                      ))}
                    </div>
                  )}

                  {/* File ID */}
                  <p className="mt-2 text-xs text-krule font-mono truncate">{doc.fileId}</p>
                </div>
              );
            })}
          </div>
        )}
      </main>
      </div>
    </div>
  );
};

export default DocumentsPage;
